import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getVisitorId } from "../lib/visitorId";
import { sessionService } from "../services/sessionService";
import { useAuth } from "../contexts/AuthContext";

function useIsPreview() {
  try {
    // Preview thường chạy trong iframe/modal hoặc có query flag.
    if (typeof window === "undefined") return false;
    const inIframe = window.self !== window.top;
    const url = new URL(window.location.href);
    const qp = (url.searchParams.get("preview") || "").toLowerCase();
    return inIframe || qp === "1" || qp === "true";
  } catch {
    return false;
  }
}

function MainLayout({ children, hideFooter = false }) {
  const location = useLocation();
  const { user } = useAuth();
  const creatingSessionRef = useRef(false);
  const lastTrackedPathRef = useRef(null);
  const isPreview = useIsPreview();
  const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

  const markActivity = () => {
    try {
      sessionStorage.setItem("tracking_last_activity_at", String(Date.now()));
    } catch {
      // ignore
    }
  };

  // 1 tab = 1 lượt truy cập (session). Nếu không hoạt động > 30 phút → lượt mới.
  useEffect(() => {
    const createTrackingSession = async () => {
      // Khung xem trước trong admin không phải khách thật
      if (isPreview) return;
      try {
        const existingSessionId = sessionStorage.getItem("tracking_session_id");
        const pending = sessionStorage.getItem("tracking_session_pending");
        const visited = sessionStorage.getItem("visited") === "1";
        const lastActivityAt = Number(sessionStorage.getItem("tracking_last_activity_at") || "0");
        const expired = lastActivityAt > 0 && Date.now() - lastActivityAt > INACTIVITY_TIMEOUT_MS;

        // Nếu đã visited trong tab và chưa expired, không tăng lượt nữa.
        if (visited && !expired) {
          markActivity();
          return;
        }

        // Nếu đang tạo session thì không tạo lại
        if (pending || creatingSessionRef.current) return;

        creatingSessionRef.current = true;
        sessionStorage.setItem("tracking_session_pending", "1");

        const payload = {
          user_id: user?._id || null,
          visitor_id: user?._id ? "" : getVisitorId(),
          device: /mobile/i.test(navigator.userAgent) ? "mobile" : "desktop",
          browser: navigator.userAgent,
          os: navigator.platform,
          referrer: document.referrer || "direct",
          source: document.referrer ? "Referrer" : "Direct",
          landing_page: window.location.pathname,
          exit_page: "",
          page_views: 1,
          duration: 0,
          converted: false,
          tracking_v: 2, // page_views tăng theo từng trang + có ping (xem sessionRoutes)
        };

        // Nếu có session cũ mà đã expired: kết thúc nhẹ (best-effort)
        if (existingSessionId) {
          const startedAt = sessionStorage.getItem("tracking_session_started_at");
          const duration = startedAt
            ? Math.max(0, Math.floor((Date.now() - Number(startedAt)) / 1000))
            : 0;
          sessionService
            .endSession(null, existingSessionId, {
              exit_page: window.location.pathname,
              page_views: 1,
              duration,
            })
            .catch(() => {});
        }

        const created = await sessionService.createSession(null, payload);

        if (created?._id) {
          sessionStorage.setItem("tracking_session_id", created._id);
          sessionStorage.setItem("tracking_session_started_at", String(Date.now()));
          sessionStorage.setItem("visited", "1");
          markActivity();
        }
      } catch (error) {
        console.error("Create session error:", error);
      } finally {
        creatingSessionRef.current = false;
        sessionStorage.removeItem("tracking_session_pending");
      }
    };

    createTrackingSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, INACTIVITY_TIMEOUT_MS]); // only on mount / auth change

  // Mark activity on navigation within SPA + đếm số trang đã xem trong phiên
  useEffect(() => {
    markActivity();
    const path = `${location.pathname}${location.search}`;
    const sessionId = sessionStorage.getItem("tracking_session_id");
    if (lastTrackedPathRef.current !== null && lastTrackedPathRef.current !== path && sessionId) {
      sessionService.trackPageview(sessionId, path).catch(() => {});
    }
    lastTrackedPathRef.current = path;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Mark activity on user interactions (keep session alive)
  useEffect(() => {
    const handler = () => markActivity();
    const events = ["click", "keydown", "scroll", "mousemove", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    const onVisible = () => {
      if (document.visibilityState === "visible") markActivity();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nhịp 60s khi tab đang hiển thị và người dùng còn thao tác trong 2 phút gần nhất
  useEffect(() => {
    if (isPreview) return undefined;
    const id = setInterval(() => {
      const sessionId = sessionStorage.getItem("tracking_session_id");
      const last = Number(sessionStorage.getItem("tracking_last_activity_at") || "0");
      if (!sessionId || document.visibilityState !== "visible") return;
      if (Date.now() - last > 2 * 60 * 1000) return;
      sessionService.ping(sessionId).catch(() => {});
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [isPreview]);

  // Khi rời trang (pagehide) => kết thúc session
  useEffect(() => {
    const handlePageHide = async () => {
      try {
        const sessionId = sessionStorage.getItem("tracking_session_id");
        const startedAt = sessionStorage.getItem("tracking_session_started_at");

        if (!sessionId || !startedAt) return;

        const duration = Math.max(
          0,
          Math.floor((Date.now() - Number(startedAt)) / 1000)
        );

        await sessionService.endSession(null, sessionId, {
          exit_page: window.location.pathname,
          page_views: 1,
          duration,
        });

        sessionStorage.removeItem("tracking_session_id");
        sessionStorage.removeItem("tracking_session_started_at");
        sessionStorage.removeItem("tracking_session_pending");
        sessionStorage.removeItem("tracking_last_activity_at");
        sessionStorage.removeItem("visited");
      } catch (error) {
        console.error("End session error:", error);
      }
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  return (
    <div
      className={
        isPreview
          ? "flex flex-col bg-white"
          : "flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-white"
      }
    >
      <main
        className={
          isPreview
            ? "flex flex-col"
            : "flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain"
        }
      >
        {isPreview ? (
          children
        ) : (
          <>
            <Header />
            {/* Không dùng flex-1/min-h-0 ở đây — sẽ khiến footer bị kẹt giữa viewport và phần nội dung dài (vd. /areas) */}
            <div className="relative z-0 w-full min-w-0 isolate">{children}</div>
            {!hideFooter && (
              <div className="relative z-10 mt-auto shrink-0">
                <Footer />
              </div>
            )}
          </>
        )}
      </main>
      {isPreview && !hideFooter && <Footer />}
    </div>
  );
}

export default MainLayout;